import { AppPermission } from "@closerplatform/spinner-openapi";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { staticConfig } from "@fidget/common/config/static-config";
import { intl } from "@fidget/common/i18n/TypedIntl";
import classNames from "classnames";
import { uniq } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import commonStyles from "../../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../../models";
import settingsStyles from "../../../../styles/settings.css";
import { SettingsForm } from "../../settingsForm";
import { SubtitleDescription } from "../../subtitleDescription";
import { PermissionsGroup } from "../permissionsGroup/permissionsGroup";
import { PermissionField, permissionGroupsList } from "../permissionsList";
import { RoleForm } from "../types";
import styles from "./roleEdit.css";

interface Props {
  role: RoleForm;
  onBack: () => void;
  save: (form: RoleForm) => Promise<void>;
}
export const RoleEdit = observer(function RoleEditComponent({ role, save, onBack }: Props) {
  const [initialRoleName] = useState(role.$.name.$);
  const [wasEdited, setWasEdited] = useState(false);
  const { uiStore } = useStore();

  useEffect(() => {
    const setEdited = () => setWasEdited(true);

    role.$.name.onUpdate(setEdited);
    role.$.permissions.onUpdate(setEdited);
  }, [role]);

  const onGoBackClick = () => {
    if (wasEdited) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    onBack();
  };

  const processAddPermissions = (
    rolePermissions: AppPermission[],
    { permissions, antagonisticPermissions }: PermissionField
  ): AppPermission[] => {
    const withAddedPermissions = [...rolePermissions, ...permissions];
    const withFilteredAntagonistic = antagonisticPermissions
      ? withAddedPermissions.filter((p) => !antagonisticPermissions.includes(p))
      : withAddedPermissions;

    return uniq(withFilteredAntagonistic);
  };

  const processRemovePermissions = (
    rolePermissions: AppPermission[],
    { permissions }: PermissionField
  ) => {
    return uniq(rolePermissions.filter((p) => !permissions.includes(p)));
  };

  const handlePermissionFieldSelected = (permissionField: PermissionField, selected: boolean) => {
    const updatedPermissions = selected
      ? processAddPermissions(role.$.permissions.$, permissionField)
      : processRemovePermissions(role.$.permissions.$, permissionField);

    role.$.permissions.reset(updatedPermissions);
  };

  const handleReset = () => {
    role.reset();
    setWasEdited(false);
  };

  const handleSubmit = async () => {
    await save(role);

    setWasEdited(false);
  };

  const renderTitle = () => (
    <div className={classNames(commonStyles.title, settingsStyles.pointer)} onClick={onGoBackClick}>
      <i className={classNames("icon-arrow", settingsStyles.rotated)} />
      {intl.formatMessage("settings.rolesSettings.label")} {initialRoleName}
    </div>
  );

  const renderNameSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.rolesSettings.name")}
        description={intl.formatMessage("settings.rolesSettings.name.description")}
      />
      <InputGroup>
        <EditInput
          value={role.$.name.value}
          onChange={(ev) => role.$.name.onChange(ev.currentTarget.value)}
          className={classNames(commonStyles.input, styles["input-width"])}
          hasError={role.$.name.hasError}
          errorHint={role.$.name.error}
          placeholder={intl.formatMessage("settings.rolesSettings.name.placeholder")}
          maxLength={staticConfig.roleNameMaxCharacters}
        />
      </InputGroup>
    </div>
  );

  const renderPermissionsList = () => {
    return (
      <>
        {permissionGroupsList.map(({ textId: labelL10nId, permissionField }) => {
          const isSet = !!permissionField.permissions.every((p) =>
            role.$.permissions.$.includes(p)
          );
          const isAntagonistSet = !!permissionField.antagonisticPermissions?.every((p) =>
            role.$.permissions.$.includes(p)
          );

          const isSelected = isSet && !isAntagonistSet;

          return (
            <PermissionsGroup
              key={labelL10nId}
              textId={permissionField.textId}
              name={intl.formatMessage(labelL10nId)}
              onSelectedChange={(selected) =>
                handlePermissionFieldSelected(permissionField, selected)
              }
              isSelected={isSelected}
            />
          );
        })}
      </>
    );
  };

  const renderPermissionsDescription = () => {
    return (
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.rolesSettings.permissions")}
          description={intl.formatMessage("settings.rolesSettings.permissions.description")}
        />
      </div>
    );
  };

  return (
    <SettingsForm
      disabled={!wasEdited}
      onDiscard={handleReset}
      onSubmit={handleSubmit}
      uiStore={uiStore}
    >
      {renderTitle()}
      {renderNameSection()}
      {renderPermissionsDescription()}
      {renderPermissionsList()}
    </SettingsForm>
  );
});
